import { Module } from 'vuex';
import {
  HouseType,
  HouseSize,
  HouseConfiguration,
  ConfigurationApi,
  Selection,
  CustomerApi,
  Floor,
  BABApi
} from '@/utils/api';
import { AxiosResponse } from 'axios';
import { RootState } from '..';
import { FormFieldData } from '@/components/FormField/formfield.models';
import i18n from '@/i18n';
import { NotificationType } from '@/components/NotificationBanner/NotificationBanner.models';
import Vue from 'vue';
import getFloorByID from '@/utils/helpers/getFloorByID';

const SET_TYPES = 'SET_TYPES';
const SELECT_TYPE = 'SELECT_TYPE';

const SET_SIZES = 'SET_SIZES';
const SELECT_SIZE = 'SELECT_SIZE';

const SET_CONFIGURATION = 'SET_CONFIGURATION';
const CLEAR_SELECTIONS = 'CLEAR_SELECTIONS';
const RESET_STATE = 'RESET_STATE';

const SET_API_LOADING = 'SET_API_LOADING';

const SET_FROM_NULL = 'SET_FROM_NULL';
const SET_VISITED_ZONES = 'SET_VISITED_ZONES';

const vueTrackingInstance = new Vue();

interface SetByCodeOptions {
  code: string;
  track?: boolean;
}

export interface ConfigurationState {
  types: HouseType[];
  selectedTypeID: string;

  sizes: HouseSize[];
  selectedSizeID: string;

  houseConfiguration?: HouseConfiguration;
  apiLoading: boolean;

  fromNull: boolean;
  visitedZones: string[];
}

const configurationInitialState = (): ConfigurationState => ({
  types: [],
  selectedTypeID: '',
  sizes: [],
  selectedSizeID: '',
  houseConfiguration: undefined,
  apiLoading: false,
  fromNull: false,
  visitedZones: []
});

const configurationApi = new ConfigurationApi();
const customerApi = new CustomerApi();
const BabApi = new BABApi();

export const configurationModule: Module<ConfigurationState, RootState> = {
  namespaced: true,
  state: configurationInitialState(),
  getters: {
    selectedType(state): HouseType | undefined {
      return state.types.find(type => type.ID === (state.selectedTypeID ?? state.houseConfiguration?.typeID));
    },
    selectedSize(state): HouseSize | undefined {
      return state.sizes.find(size => size.ID === (state.selectedSizeID ?? state.houseConfiguration?.sizeID));
    },
    currentCode(state): string | undefined {
      return state.houseConfiguration?.baufritzCode;
    },
    shareLink(_, { currentCode }: { currentCode: string }, __, { 'app/linkBaseUrl': baseUrl }): string {
      return `${baseUrl}${currentCode.toUpperCase()}`;
    },
    floorByIDs(state): (floorID: string, zoneID?: string) => Floor | undefined {
      return (floorID: string, zoneID?: string): Floor | undefined => {
        return getFloorByID(state.houseConfiguration?.floors || [], floorID, zoneID);
      };
    }
  },
  actions: {
    async setTypes({ commit, dispatch, rootState }) {
      const typesResponse: AxiosResponse<HouseType[]> = await configurationApi.getTypes(rootState.app.locale);
      const types: HouseType[] = typesResponse.data;

      commit(SET_TYPES, types);

      if (types.length) {
        dispatch('selectType', types[0].ID);
      }

      return types;
    },
    selectType({ commit }, typeID: string) {
      commit(SELECT_TYPE, typeID);
    },
    async fetchSizes({ commit, rootGetters, rootState }) {
      const sizesResponse: AxiosResponse<HouseSize[]> = await configurationApi.getSizes(
        rootGetters['configuration/selectedType'].ID,
        rootState.app.locale
      );
      const sizes: HouseSize[] = sizesResponse.data;

      commit(SET_SIZES, sizes);

      return sizes;
    },
    async setSizes({ dispatch, state, rootGetters }) {
      if (!state.sizes.length && rootGetters['configuration/selectedType']) {
        const sizes = await dispatch('fetchSizes');

        if (sizes.length) {
          await dispatch('selectSize', sizes[0].ID);
        }
      }
    },
    async selectSize({ commit, dispatch }, sizeID: string) {
      commit(SELECT_SIZE, sizeID);
      commit(CLEAR_SELECTIONS);

      await dispatch('updateConfiguration', { update: null });

      dispatch('app/clearCompletedSteps', null, { root: true });

      return;
    },

    async setConfigurationByCode({ commit, dispatch, rootState }, params: SetByCodeOptions | string) {
      // always track if not given
      let track = true;
      let code;
      // as well support string or object call for legacy behaviour
      if (typeof params == 'object') {
        code = params.code;
        track = params.track ?? true;
      } else {
        code = params;
      }
      const configurationResponse: AxiosResponse<HouseConfiguration> = await configurationApi.getConfiguration(
        code,
        rootState.app.locale,
        undefined,
        {
          validateStatus: (status: number) => [200, 404].includes(status)
        }
      );
      const configuration = configurationResponse.data;

      if (configuration) {
        await dispatch('fetchSizes');

        commit(SET_CONFIGURATION, configuration);
        commit(SELECT_TYPE, configuration.typeID);
        commit(SELECT_SIZE, configuration.sizeID);
      }

      if (track) {
        vueTrackingInstance.$gtm.trackLoadByCode(code);
      }

      return configuration;
    },
    async updateConfiguration(
      { commit, dispatch, state, getters, rootState },
      payload: { update?: Selection[]; resolveConflicts?: boolean }
    ) {
      if (getters.selectedType && getters.selectedSize) {
        try {
          const configurationResponse: AxiosResponse<HouseConfiguration> = await configurationApi.putConfiguration(
            getters.selectedType.ID,
            getters.selectedSize.ID,
            rootState.app.locale,
            {
              selections: state.houseConfiguration?.selections ?? [],
              update: payload.update
            }
          );

          const configuration = configurationResponse.data;

          commit(SET_CONFIGURATION, configuration);

          return configuration;
        } catch (error) {
          const errorResponse: AxiosResponse = error.response;

          if (errorResponse.status === 409) {
            const configuration = errorResponse.data as HouseConfiguration;
            const fixes = configuration.conflicts && configuration.conflicts[0] && configuration.conflicts[0].fixes[0];
            const zoneIDs = fixes?.steps.map(step => step.selection.zoneID) || [];

            const resolveConflicts = payload.resolveConflicts && !zoneIDs.find(ID => state.visitedZones.includes(ID));

            if (resolveConflicts) {
              dispatch('updateConfiguration', {
                update: fixes?.steps.map(step => step.selection) || []
              });
            } else {
              dispatch('notify/openConflictModal', configuration.conflicts || [], { root: true });
            }
          }
        }
      }
    },
    async sendEmail({ dispatch, rootState, rootGetters }, email: FormFieldData) {
      if (rootState.configuration.houseConfiguration?.baufritzCode) {
        const shareLink = rootGetters['configuration/shareLink'];
        try {
          await customerApi.postShare(rootState.app.locale, {
            receiver: email.value,
            baufritzCode: rootState.configuration.houseConfiguration?.baufritzCode,
            url: shareLink
          });
          dispatch(
            'notify/showNotification',
            {
              headline: i18n.t('components.latestCode.emailSuccess'),
              type: NotificationType.Success,
              origin: 'ConfigCodeDialog',
              key: 'latestCode/emailSuccess'
            },
            { root: true }
          );
        } catch (error) {
          console.error(error);
        }
      } else {
        dispatch(
          'notify/showNotification',
          {
            headline: i18n.t('components.latestCode.emailNoCode'),
            type: NotificationType.Error,
            origin: 'ConfigCodeDialog',
            key: 'LatestCodeDisplay/emailNoCode'
          },
          { root: true }
        );
      }
    },
    async getPDF({ rootState }, code: string) {
      const response = await configurationApi.getPDF(code, rootState.app.locale, undefined, {
        responseType: 'blob',
        validateStatus: (status: number) => [200, 404].includes(status)
      });

      if (response.status === 200 && response.data) {
        const PDFUrl = URL.createObjectURL(response.data);

        return PDFUrl;
      } else {
        return '';
      }
    },
    async getBAB({ rootState }) {
      const response = await BabApi.getBAB(rootState.app.locale, undefined, {
        responseType: 'blob',
        validateStatus: (status: number) => [200, 404].includes(status)
      });

      if (response.status === 200 && response.data) {
        const PDFUrl = URL.createObjectURL(response.data);

        return PDFUrl;
      } else {
        return '';
      }
    },
    setApiLoading({ commit }, value: boolean) {
      commit(SET_API_LOADING, value);
    },
    setFromNull({ commit }, value: boolean) {
      commit(SET_FROM_NULL, value);
    },
    setVisitedZones({ commit }, zoneID: string) {
      commit(SET_VISITED_ZONES, zoneID);
    },
    resetState({ commit }) {
      commit(RESET_STATE);
    }
  },
  mutations: {
    [SET_TYPES](state, types: HouseType[]) {
      state.types = types;
    },
    [SELECT_TYPE](state, typeID: string) {
      state.selectedTypeID = typeID;
    },

    [SET_SIZES](state, sizes: HouseSize[]) {
      state.sizes = sizes;
    },
    [SELECT_SIZE](state, sizeID: string) {
      state.selectedSizeID = sizeID;
    },
    [CLEAR_SELECTIONS](state) {
      if (state.houseConfiguration) {
        state.houseConfiguration.selections = [];
      }
    },
    [SET_CONFIGURATION](state, configuration: HouseConfiguration) {
      state.houseConfiguration = configuration;
    },
    [SET_API_LOADING](state, value: boolean) {
      state.apiLoading = value;
    },
    [SET_FROM_NULL](state, value: boolean) {
      state.fromNull = value;
    },
    [SET_VISITED_ZONES](state, zoneID: string) {
      if (!state.visitedZones.includes(zoneID)) {
        state.visitedZones = [...state.visitedZones, zoneID];
      }
    },
    [RESET_STATE](state) {
      Object.assign(state, configurationInitialState());
    }
  }
};
